import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createBrowserRouter, RouterProvider, Outlet, useParams } from 'react-router-dom';
import { ProducerView } from './components/ProducerView';
import { SpectatorView } from './components/SpectatorView';
import { LiveEventView } from './components/LiveEventView';
import { SignupForm } from './components/SignupForm';
import { EventWebpage } from './components/EventWebpage';
import { PresenterView } from './components/PresenterView';
import { Api } from './utils/api';

// Create a client
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60, // 1 minute
            refetchOnWindowFocus: false,
        },
    },
});

const api = new Api();

// Layout component without navigation
function Layout() {
    return (
        <div className="min-h-screen bg-background">
            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="max-w-4xl mx-auto">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}

// Wrapper component to handle URL parameters
function SpectatorViewWrapper() {
    const { eventStateId } = useParams();
    return <LiveEventView api={api} eventStateId={eventStateId} />;
}

// Wrapper component for signup form
function SignupFormWrapper() {
    const { eventId } = useParams();
    return <SignupForm api={api} eventId={eventId} />;
}

// Wrapper component for event webpage
function EventWebpageWrapper() {
    return <EventWebpage />;
}

// Standalone signup form wrapper (no header)
function StandaloneSignupFormWrapper() {
    const { eventId } = useParams();
    return (
        <div className="min-h-screen bg-background">
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <SignupForm api={api} eventId={eventId} />
            </main>
        </div>
    );
}

// Wrapper component for presenter view
function PresenterViewWrapper() {
    const { eventId } = useParams();
    const [eventStateId, setEventStateId] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        const fetchEventState = async () => {
            try {
                const stateId = await api.getOrCreateEventState(eventId);
                setEventStateId(stateId);
            } catch (err) {
                console.error('Failed to get event state:', err);
                setError('Failed to get event state');
            } finally {
                setLoading(false);
            }
        };

        if (eventId) {
            fetchEventState();
        }
    }, [eventId]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-red-500">{error}</div>
            </div>
        );
    }

    return <PresenterView api={api} eventId={eventId} eventStateId={eventStateId} />;
}

const router = createBrowserRouter([
    {
        element: <Layout />,
        children: [
            {
                path: "/",
                element: <ProducerView api={api} />,
            },
            {
                path: "/producer/event/:eventId",
                element: <ProducerView api={api} />,
            },
            {
                path: "/producer/event/:eventId/presenter",
                element: <PresenterViewWrapper />,
            },
        ],
    },
    // Standalone routes that don't use Layout
    {
        path: "/spectator/:eventStateId",
        element: <SpectatorViewWrapper />,
    },
    {
        path: "/signup/:eventId",
        element: <StandaloneSignupFormWrapper />,
    },
    {
        path: "/event/:eventId/webpage",
        element: <EventWebpageWrapper />,
    },
]);

export default function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
        </QueryClientProvider>
    );
}
